import type { ReactNode } from 'react'
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import LayoutContainer from 'components/Common/LayoutContainer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import Places, { getPlaceByRegionCode } from 'constants/places'
import GeoContext from 'contexts/geoContext'
import ExperiencesSearchDateQuickChips, {
  CHIP_ANYTIME,
  type ExperiencesDateQuickChipSelectPayload,
} from 'components/Search/Experiences/ExperiencesSearchDateQuickChips'
import ExperienceCompactSearchTile from 'components/Experiences/ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from 'components/Experiences/ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import useExperienceHomepagePlace from 'components/Experiences/hooks/useExperienceHomepagePlace'
import { useResolvedPlaceIdentity, usePlaceAncestorsById } from 'hooks/usePlace'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { useInView } from 'react-intersection-observer'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { useAppSelector } from 'hooks/reduxHooks'
import { getListName } from 'analytics/enhanced-ecommerce'
import { EmptyArray, fillArray, without } from 'lib/array/arrayUtils'
import { getExperiencePlaceShortName, getLuxPlusExperienceSearchPlace, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { LUX_PLUS_MEMBER_PRICES_FILTER } from 'lib/experiences/luxPlusFeatureFilterUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import variables from 'styles/vendor/bootstrap/variables'

import {
  getExperiencesHomepageUpliftListName,
  useExperiencesHomepageUpliftTracking,
} from './experiencesHomepageUpliftAnalytics'

const OFFERS_LIMIT = 8
const MIN_OFFERS_TO_SHOW = 1
const TABLET_TILE_WIDTH = 270
const layoutContainerPadding = variables.gutterWidth / 2

const messages = defineMessages({
  sectionTitle: {
    id: '2PwsFDSAw5dfzTQE-kbx',
    defaultMessage: 'Top <luxPlus>LuxPlus+</luxPlus> discount offers in <i>{placeName}</i>',
    description: 'Section heading for LuxPlus member discount experience offers on the Experiences homepage uplift',
  },
  viewAllCta: {
    id: 'CR1DN9VSbxhInle933gj',
    defaultMessage: 'View all offers',
    description: 'Header tertiary call-to-action linking to LuxPlus member-priced experience search results on homepage uplift',
  },
  luxPlusLogoLabel: {
    id: '8jL8udZb9UfCr_dgOqSN',
    defaultMessage: 'LuxPlus+',
    description: 'Accessible name for the inline LuxPlus wordmark in the Experiences homepage LuxPlus top offers section heading',
  },
  noOffersForFilter: {
    id: 'd0bPzchDk2Y0vLQzEyBo',
    defaultMessage: 'No LuxPlus+ offers available for {dateLabel}',
    description: 'Empty state in LuxPlus top offers when the selected date quick chip returns no member-priced experiences',
  },
})

const Intro = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: ${rem(12)};
  row-gap: ${rem(24)};
  align-items: start;
  inline-size: 100%;

  ${mediaQueryUp.tablet} {
    row-gap: ${rem(12)};
    column-gap: ${rem(20)};
  }
`

const Title = styled(Heading)`
  grid-column: 1;
  grid-row: 1;
  min-inline-size: 0;
`

const HeaderAction = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`

const StyledDateChips = styled(ExperiencesSearchDateQuickChips)`
  grid-column: 1 / -1;
  grid-row: 2;
`

const OffersCarousel = styled(CardCarousel)`
  ${mediaQueryUp.tablet} {
    > div > div {
      column-gap: ${rem(20)};
    }
  }
`

const EmptyFilterState = styled(Group)`
  padding: ${rem(24)} 0;
`

const TileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  > * {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
  }

  [data-testid='experience-offer-tile'] {
    flex: 1;
    height: 100%;
  }

  [data-testid='experience-offer-tile'] > :first-child {
    align-self: stretch;
  }
`

function ExperiencesHomepageLuxPlusTopOffers() {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const { place, placeIdentity } = useExperienceHomepagePlace()
  const searchPlaceIdentity = useMemo(
    () => getLuxPlusExperienceSearchPlace(placeIdentity),
    [placeIdentity],
  )
  const searchPlace = useResolvedPlaceIdentity(searchPlaceIdentity) ?? place
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const [checkIn, setCheckIn] = useState<string | undefined>()
  const [checkOut, setCheckOut] = useState<string | undefined>()
  const [trackingRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const trackInteraction = useExperiencesHomepageUpliftTracking('luxplus_top_offers')

  const placeNameShort = useMemo(
    () => (searchPlace ? getExperiencePlaceShortName(searchPlace) : ''),
    [searchPlace],
  )

  const regionPlace = useMemo(
    () => getPlaceByRegionCode(geo.currentRegionCode),
    [geo.currentRegionCode],
  )
  const searchPlaceId = searchPlaceIdentity.id
  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(searchPlaceId)

  const fallbackPlaceIds = useMemo(() => {
    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...new Set([...ancestorIds, regionPlace.id])]
  }, [ancestorPlaceIds, regionPlace])

  const checkinDate = useMemo(
    () => (checkIn ? moment(checkIn, ISO_DATE_FORMAT) : undefined),
    [checkIn],
  )
  const checkoutDate = useMemo(
    () => (checkOut ? moment(checkOut, ISO_DATE_FORMAT) : undefined),
    [checkOut],
  )
  const selectedDateFilterLabel = useExperienceDateDisplayValue(checkinDate, checkoutDate)

  const handleDateChipSelect = useCallback((payload: ExperiencesDateQuickChipSelectPayload) => {
    trackInteraction('luxplus_top_offers', 'date_filter_selected')
    if (payload.value === CHIP_ANYTIME || !payload.range) {
      setCheckIn(undefined)
      setCheckOut(undefined)
      return
    }
    setCheckIn(payload.range.start.format(ISO_DATE_FORMAT))
    setCheckOut(payload.range.end.format(ISO_DATE_FORMAT))
  }, [trackInteraction])

  const [list, experiences] = useExperienceSearch(
    {
      placeId: searchPlaceId,
      limit: OFFERS_LIMIT,
      sortBy: 'type',
      from: checkIn,
      to: checkOut,
      enabled: canViewLuxPlusBenefits,
      fallbackPlaceIds: fetchingAncestors ? EmptyArray : fallbackPlaceIds,
    },
    {
      luxPlusFeatures: [LUX_PLUS_MEMBER_PRICES_FILTER],
    },
  )

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', getExperiencesHomepageUpliftListName('luxplus_top_offers')),
    { key: searchPlaceId },
  )

  const searchUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: searchPlaceId,
      destinationName: placeNameShort,
      luxPlusFeatures: LUX_PLUS_MEMBER_PRICES_FILTER,
      ...(checkIn && { checkIn }),
      ...(checkOut && { checkOut }),
    })
    return `/search/experiences?${query}`
  }, [searchPlaceId, placeNameShort, checkIn, checkOut])

  const tileDate = checkIn && checkIn === checkOut ? checkIn : undefined
  const isFetching = !list || list.fetching
  const hasEnoughOffers = !!list && !list.fetching && list.ids.length >= MIN_OFFERS_TO_SHOW
  const isAnytime = !checkIn && !checkOut
  const showEmptyFilterState =
    !isFetching && !isAnytime && list && list.ids.length === 0

  const trackViewAllClick = useCallback(() => {
    trackInteraction('luxplus_top_offers', 'view_all_clicked')
  }, [trackInteraction])

  useEffect(() => {
    if (inView && config.BRAND === LUXURY_ESCAPES && canViewLuxPlusBenefits) {
      trackInteraction('luxplus_top_offers', 'impression')
    }
  }, [canViewLuxPlusBenefits, inView, trackInteraction])

  if (config.BRAND !== LUXURY_ESCAPES || !canViewLuxPlusBenefits) {
    return null
  }

  if (!isFetching && isAnytime && !hasEnoughOffers) {
    return null
  }

  const skeletonKeys = fillArray(OFFERS_LIMIT)

  const viewAllLabel = intl.formatMessage(messages.viewAllCta)
  const noOffersForFilterLabel = intl.formatMessage(messages.noOffersForFilter, {
    dateLabel: selectedDateFilterLabel ?? '',
  })

  const renderOfferTileContent = (experience: typeof experiences[number], idx: number) => {
    if (!experience) {
      return <ExperienceCompactSearchTileSkeleton />
    }

    return (
      <ExperienceCompactSearchTile
        experience={experience}
        position={idx}
        date={tileDate}
        listKey={list.listKey}
      />
    )
  }

  const renderOfferTiles = (wrapTile: (key: string | number, content: ReactNode) => ReactNode) => {
    if (isFetching) {
      return skeletonKeys.map((id) => wrapTile(id, <ExperienceCompactSearchTileSkeleton />))
    }

    return experiences.map((experience, idx) => wrapTile(
      `${experience?.id ?? 'skeleton'}-${idx}`,
      renderOfferTileContent(experience, idx),
    ))
  }

  return (
    <LayoutContainer ref={trackingRef}>
      <Group direction="vertical" gap={20}>
        <Intro>
          <Title variant="heading3">
            {intl.formatMessage(messages.sectionTitle, {
              placeName: placeNameShort,
              luxPlus: () => (
                <LuxPlusLogo
                  key="lux-plus"
                  inline
                  height={12}
                  role="img"
                  aria-label={intl.formatMessage(messages.luxPlusLogoLabel)}
                />
              ),
              i: (chunks: ReactNode) => <i key="place">{chunks}</i>,
            })}
          </Title>
          <HeaderAction>
            <CSSBreakpoint max="mobile">
              <TextButton
                kind="tertiary"
                size="small"
                horizontalOutdent="end"
                to={searchUrl}
                onClick={trackViewAllClick}
              >
                {viewAllLabel}
              </TextButton>
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <TextButton
                kind="tertiary"
                size="medium"
                horizontalOutdent="end"
                to={searchUrl}
                onClick={trackViewAllClick}
              >
                {viewAllLabel}
              </TextButton>
            </CSSBreakpoint>
          </HeaderAction>

          <StyledDateChips
            onSelect={handleDateChipSelect}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            dateSearchOptionId={!checkIn && !checkOut ? DATE_SEARCH_OPTION_IDS.ANYTIME : undefined}
            displayType="single-line"
            showNextBooking={false}
            showToday
          />
        </Intro>

        <OfferListEventsProvider tracking={tracking}>
          {showEmptyFilterState ? (
            <EmptyFilterState direction="vertical" horizontalAlign="center" fullWidth>
              <Heading variant="heading4" colour="neutral-two">
                {noOffersForFilterLabel}
              </Heading>
            </EmptyFilterState>
          ) : (
            <>
              <CSSBreakpoint max="mobile">
                <Group direction="vertical" gap={16}>
                  {renderOfferTiles((key, content) => (
                    <Fragment key={key}>{content}</Fragment>
                  ))}
                </Group>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <OffersCarousel
                  pageSize="auto"
                  width={rem(TABLET_TILE_WIDTH)}
                  tabletWidth={rem(TABLET_TILE_WIDTH)}
                  desktopWidth={rem(TABLET_TILE_WIDTH)}
                  gap={16}
                  gutterSize={layoutContainerPadding}
                  gutterStyle="overflow"
                  snap="start"
                >
                  {renderOfferTiles((key, content) => (
                    <TileWrapper key={key}>{content}</TileWrapper>
                  ))}
                </OffersCarousel>
              </CSSBreakpoint>
            </>
          )}
        </OfferListEventsProvider>
      </Group>
    </LayoutContainer>
  )
}

export default ExperiencesHomepageLuxPlusTopOffers
